import {
	useId,
	useRef,
	useState,
	type ReactNode,
} from 'react';
import { cn } from '@uconnect/assets/lib/utils';
import Icon from '@uconnect/assets/components/ui/icon';
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@uconnect/assets/components/ui/accordion';
import { useAui } from '@assistant-ui/react';
import { Button } from '@uconnect/assets/components/ui/button';
import { useFittedChipCount, useIsTruncated } from '@uconnect/assets/hooks/useChipFit';
import PROMPT_DEFINITIONS from '../prompt-definitions.json';
import Feature from '../../common/js/components/Feature';
import type { PersonalizationSummary } from '../../common/js/types';

const DEFAULT_PROMPT_KEYS = [ 'career-paths', 'opportunities', 'interview' ];

// Must match the chip row's `gap-1.5` class below.
const CHIP_ROW_GAP_PX = 6;
const CHIP_ROW_MAX_LINES = 2;

const GREETINGS = [
	'Hey',
	'Hi there',
	'Hello again',
	'Nice to see you',
	'Good to see you',
	'Welcome back',
	'Glad you\'re here',
	'Today\'s your day',
	'Ready to explore',
	'Let\'s get started',
	'Made for you',
	'Ready for your next step',
];

function getRandomGreeting(): string {
	return GREETINGS[ Math.floor( Math.random() * GREETINGS.length ) ];
}

function PersonalizationChip( {
	children,
	dataFitChip,
	dataFitOverflow,
	title,
	titleOnlyWhenTruncated,
}: {
	children: ReactNode;
	dataFitChip?: boolean;
	dataFitOverflow?: boolean;
	title?: string;
	// When set, `title` is only rendered once the chip's own text is actually clipped,
	// rather than as an always-on tooltip.
	titleOnlyWhenTruncated?: boolean;
} ) {
	// `text-overflow: ellipsis` only takes effect on a block container's own inline
	// content, not on a flex container's children — so the truncating styles (and the
	// scrollWidth/clientWidth measurement) live on this inner block span, not the outer
	// `inline-flex` pill.
	const textRef = useRef<HTMLSpanElement>( null );
	const isTruncated = useIsTruncated( textRef, [ children ] );

	const chipTitle = titleOnlyWhenTruncated && ! isTruncated ? undefined : title;

	return (
		<span
			role="listitem"
			data-fit-chip={ dataFitChip ? 'true' : undefined }
			data-fit-overflow={ dataFitOverflow ? 'true' : undefined }
			title={ chipTitle }
			className={ cn(
				'inline-flex! shrink-0! items-center! rounded-full! border! border-foreground/30!',
				'bg-white! px-2.5! py-1! text-xs! font-medium! text-foreground!',
				'max-w-[200px]!',
			) }
		>
			<span
				ref={ textRef }
				className="block! min-w-0! overflow-hidden! text-ellipsis! whitespace-nowrap!"
			>
				{ children }
			</span>
		</span>
	);
}

function PersonalizationHero( {
	firstName,
	personalization,
	contentPreferencesUrl,
}: {
	firstName?: string | null;
	personalization?: PersonalizationSummary | null;
	contentPreferencesUrl?: string;
} ) {
	// Picked once per mount (not on every re-render) so it doesn't change underneath the
	// user while the chip row re-measures on resize.
	const [ greeting ] = useState( getRandomGreeting );
	const personalizationHeadingId = useId();

	// Order matters: user type and class year first, then communities (academic/career
	// interests), then content-type preferences — each group can still get truncated by
	// the fitting logic below if it doesn't fit.
	const allChips = [
		personalization?.userTypeLabel,
		personalization?.classYearLabel,
		...( personalization?.communityNames ?? [] ),
		...( personalization?.contentPreferenceNames ?? [] ),
	].filter( ( chip ): chip is string => Boolean( chip ) );

	// Worst-case remainder (if none of the chips end up fitting), used only to reserve
	// enough width for the "+N" chip; the real displayed count is computed below.
	const overflowPlaceholder = `+${ allChips.length }`;

	const { containerRef, measureRef, visibleCount } = useFittedChipCount( allChips.join( '|' ), {
		gapPx: CHIP_ROW_GAP_PX,
		maxLines: CHIP_ROW_MAX_LINES,
		overflowPlaceholder,
	} );

	const visibleChips = allChips.slice( 0, Math.min( visibleCount, allChips.length ) );
	const remainingCount = allChips.length - visibleChips.length;
	const remainingLabels = allChips.slice( visibleChips.length );

	// Chip labels aren't guaranteed unique (e.g. two communities could share a name), so
	// disambiguate repeats with a stable per-label occurrence count rather than the map
	// index, keeping keys tied to content instead of position.
	const keyChipLabels = ( labels: string[] ) => {
		const seenCounts = new Map<string, number>();
		return labels.map( ( label ) => {
			const occurrence = seenCounts.get( label ) ?? 0;
			seenCounts.set( label, occurrence + 1 );
			return { label, key: JSON.stringify( [ label, occurrence ] ) };
		} );
	};
	const keyedVisibleChips = keyChipLabels( visibleChips );
	const keyedAllChips = keyChipLabels( allChips );

	return (
		<div className="flex flex-col gap-3">
			<h2 className="m-0!">
				{ greeting }
				{ firstName && `, ${ firstName }` }
			</h2>
			{ allChips.length > 0 && (
				<div className="flex flex-col gap-2 rounded-lg bg-main-100! p-3! text-foreground!">
					<div className="flex items-center gap-2 text-main-700!">
						<Icon
							name="circleUserSparkle"
							className="block! shrink-0! size-5"
						/>
						<p
							id={ personalizationHeadingId }
							className="m-0! text-sm font-semibold!"
						>
							AI Search is now personalized to you:
						</p>
					</div>
					<div className="relative! flex flex-col gap-2 min-w-0">
						<div
							ref={ containerRef }
							role="list"
							aria-labelledby={ personalizationHeadingId }
							className="flex flex-wrap gap-1.5"
						>
							{ keyedVisibleChips.map( ( { label, key } ) => (
								<PersonalizationChip
									key={ key }
									title={ label }
									titleOnlyWhenTruncated
								>
									{ label }
								</PersonalizationChip>
							) ) }

							{ remainingCount > 0 && (
								<PersonalizationChip title={ remainingLabels.join( ', ' ) }>
									<span aria-hidden="true">
										{ `+${ remainingCount }` }
									</span>
									<span className="sr-only">
										{ ` (plus ${ remainingCount } more: ${ remainingLabels.join( ', ' ) })` }
									</span>
								</PersonalizationChip>
							) }
						</div>
						{ /* Hidden nowrap row used only to measure each chip's natural width. */ }
						<div
							ref={ measureRef }
							aria-hidden="true"
							className={ cn(
								'invisible! absolute! inset-x-0! top-0! h-0! overflow-hidden! pointer-events-none!',
								'flex! flex-nowrap! gap-1.5!',
							) }
						>
							{ keyedAllChips.map( ( { label, key } ) => (
								<PersonalizationChip
									key={ key }
									dataFitChip
								>
									{ label }
								</PersonalizationChip>
							) ) }
							<PersonalizationChip dataFitOverflow>
								{ overflowPlaceholder }
							</PersonalizationChip>
						</div>
						{ contentPreferencesUrl && (
							<a
								href={ contentPreferencesUrl }
								className="text-xs underline! text-main-700! w-fit"
							>
								Customize your preferences
							</a>
						) }
					</div>
				</div>
			) }
		</div>
	);
}

export default function EmptyThread( {
	chatDisabled = false,
	isUnauthenticatedSearchRestricted = false,
	signInUrl,
	onSignIn,
	initialPrompts,
	isLoggedIn = false,
	firstName,
	personalization,
	contentPreferencesUrl,
}: {
	chatDisabled?: boolean;
	isUnauthenticatedSearchRestricted?: boolean;
	signInUrl?: string;
	onSignIn?: () => void;
	initialPrompts?: string[];
	isLoggedIn?: boolean;
	firstName?: string | null;
	personalization?: PersonalizationSummary | null;
	contentPreferencesUrl?: string;
} = {}) {
	const { thread } = useAui();
	const activeKeys =
		initialPrompts && initialPrompts.length > 0
			? initialPrompts
			: DEFAULT_PROMPT_KEYS;
	const suggestions = activeKeys
		.map((key) => {
			const def = PROMPT_DEFINITIONS[key as keyof typeof PROMPT_DEFINITIONS];
			return def ? { ...def, key, prompt: def.label } : null;
		})
		.filter((s): s is { label: string; icon: string; module: string | null; key: string; prompt: string } => Boolean(s))
		.slice(0, 4);

	const handleSuggestionClick = (suggestion: { prompt: string; key: string }) => {
		thread.append({
			role: 'user',
			content: [{ type: 'text', text: suggestion.prompt }],
			metadata: {
				custom: {
					promptSource: 'suggested',
					promptKey: suggestion.key,
				},
			},
		});
	};
	if (isUnauthenticatedSearchRestricted) {
		return (
			<div className="flex! flex-1 flex-col items-center justify-center gap-2 text-center">
				<h2 className="m-0!">
					Sign in to unlock AI Search
				</h2>
				<p className="text-muted-foreground! m-0! text-base text-balance">
					Get personalized guidance, discover content that&apos;s relevant to you, and access search history,
					{ ' ' }
					<strong>all in one place</strong>
					.
				</p>
				<a
					href={ signInUrl }
					onClick={ onSignIn }
					className="button iconafter-ai-search flex! items-center gap-2 min-w-[50%] box-border justify-center mt-2! mb-4!"
				>
					Sign in to Search
				</a>

				<div className="grid gap-4! w-full text-left! text-pretty max-w-[380px] mx-auto!">
					<Feature
						icon="circleUserSparkle"
						title="Personalized to you"
					>
						The more you search, the smarter it gets. Get suggestions tailored to your interests
						and pick up right where you left off.
					</Feature>
					<Feature
						icon="messageText"
						title="Search conversationally"
					>
						Talk to our search like a person. Ask a full question and get a direct answer or a
						curated path for further exploration.
					</Feature>
					<Feature
						icon="database"
						title="Uses this site's content"
					>
						Results come from this website&apos;s
						{ ' ' }
						resources&thinsp;&mdash;&thinsp;not&nbsp;generic internet searches.
					</Feature>
					<Feature
						icon="sparkles"
						title="Stays accurate and current"
					>
						Search pulls from actively maintained resources and will always point you toward a next step,
						even when something is unavailable or out of scope.
					</Feature>
				</div>
			</div>
		);
	}

	if (chatDisabled) {
		return (
			<div className="flex! flex-1 flex-col items-center justify-center gap-6 text-center py-8">
				<div className="rounded-full p-6! bg-citation-background! text-citation-foreground!">
					<Icon
						name="clockRotateClockwise"
						className="size-12 stroke-2"
					/>
				</div>
				<div className="flex flex-col gap-2">
					<h2 className="m-0!">
						Daily guest limit reached
					</h2>
					<p className="text-muted-foreground! m-0!">
						Sign in to continue using AI Search.
						<br />
						Guest access resets at midnight.
					</p>
				</div>
				<a
					href={ signInUrl }
					onClick={ onSignIn }
					className="button"
				>
					Sign In
				</a>
			</div>
		);
	}

	return (
		<div className={ cn(
			'flex flex-col gap-6'
		) }
		>
			<Accordion
				type="single"
				collapsible
				className="@container"
			>
				<AccordionItem value="how-it-works">
					<AccordionTrigger
						className={ cn(
							'default hover:underline! flex! gap-2 items-center justify-end py-0!',
							'text-primary! font-semibold! text-sm!',
							'font-default! border-box! border-0! after:hidden!', // Override reset style
						) }
						iconClassName="text-inherit! size-3"
					>
						<span className="grow flex items-center justify-end gap-2">
							<Icon
								name="circleQuestion"
								className="shrink-0 size-4"
							/>
							How search works
						</span>
					</AccordionTrigger>
					<AccordionContent className="transition-all!">
						<div className="grid gap-4! w-full @xl:grid-cols-2">
							<Feature
								icon="circleUserSparkle"
								title="Personalized results when you're signed in"
							>
								Get more personalized suggestions and access to your search history
								{ ' ' }
								<strong>when you&apos;re signed in</strong>
								.
							</Feature>
							<Feature
								icon="messageText"
								title="Search conversationally"
							>
								Talk to our search like a person. Ask a full question and get a direct answer or a
								curated path for further exploration.
							</Feature>
							<Feature
								icon="database"
								title="Uses this site's content"
							>
								Results come from this website&apos;s
								{ ' ' }
								resources&thinsp;&mdash;&thinsp;not&nbsp;generic internet searches.
							</Feature>
							<Feature
								icon="sparkles"
								title="Up-to-date – stays accurate and current"
							>
								Search uses maintained resources. If something is unavailable, out of scope, or
								unclear, it will let you know and suggest next steps.
							</Feature>
						</div>
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			{ isLoggedIn ? (
				<PersonalizationHero
					firstName={ firstName }
					personalization={ personalization }
					contentPreferencesUrl={ contentPreferencesUrl }
				/>
			) : (
				<div className="flex flex-col gap-1">
					<h2 className="m-0!">
						Career Services, Always Open
					</h2>
					<p className="text-muted-foreground! m-0! text-sm text-balance">
						Try AI Search to access your school&apos;s career resources
					</p>
					<a
						href={ signInUrl }
						onClick={ onSignIn }
						className="text-sm underline! w-fit"
					>
						Log in to customize your experience
					</a>
				</div>
			) }

			<section aria-label="Search suggestions">
				<h3 className="text-sm! text-left! mb-3!">Try asking:</h3>
				<div className="grid gap-2! @sm:grid-cols-2">
					{ suggestions.map( ( suggestion ) => (
						<button
							key={ suggestion.prompt }
							type="button"
							onClick={ () => handleSuggestionClick( suggestion ) }
							className={ cn(
								'default font-default! cursor-pointer', // Reset theme button styles
								'w-full rounded-sm bg-muted p-2 text-sm!',
								'flex items-center gap-2 group',
								'bg-muted transition-colors hover:bg-main-100',
								'focus-visible:ring-ring/50 focus-visible:border-ring',
								'focus-visible:ring-3 outline-none',
							) }
						>
							<div
								className={ cn(
									'p-1.5! bg-main-200! text-main-700 rounded',
									'flex! items-center justify-center shrink-0 self-start',
								) }
							>
								<Icon
									name={ suggestion.icon }
									className="size-4 stroke-[1.25]"
								/>
							</div>
							<span className="text-left group-hover:text-main-700 transition-colors">
								{ suggestion.prompt }
							</span>
						</button>
					) ) }
				</div>
			</section>
		</div>
	);
}
